import { Tabs, Tag } from 'antd';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLifecycles } from 'react-use';

import { PageHeader } from '@ant-design/pro-components';

import {
  getIslamicSignedAgreementsThunk,
  getIslamicPendingSigningAgreementsThunk,
  getPendingTradingAgreementsThunk,
} from '../../../services/islamicAgreementService/islamicAgreementThunk';
import { AppDispatch, RootState } from '../../../store/store';
import CompletedIslamicSigningTab from './CompletedSigningTab';
import PendingIslamicSigningTab from './PendingSigningTab';

import type { TabsProps } from 'antd';
import PendingTradingTab from './PendingTradingTab';

const IslamicAgreementSigningListingPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const activeTab = queryParams.get('tab');
  const pageNum = queryParams.get('pageNum');
  const pageSize = queryParams.get('pageSize');

  const initialParams = useMemo(
    () => ({
      pagination: {
        current: 1,
        pageSize: 10,
      },
      sortOrder: 'desc',
      sortField: 'createdAt',
      search: {},
      filters: {},
    }),
    []
  );

  const { signedIslamicAgreements, pendingSigningIslamicAgreements, pendingTradingAgreements } =
    useSelector((state: RootState) => state.islamicAgreement);

  const items: TabsProps['items'] = [
    {
      key: 'pendingSigning',
      label: (
        <span>
          Pending Signing{' '}
          {pendingSigningIslamicAgreements?.data?.searchTotal != null && (
            <Tag>{pendingSigningIslamicAgreements?.data?.total}</Tag>
          )}
        </span>
      ),
      children: <PendingIslamicSigningTab />,
    },
    {
      key: 'completedSigning',
      label: (
        <span>
          Completed Signing{' '}
          {signedIslamicAgreements?.data?.searchTotal != null && (
            <Tag>{signedIslamicAgreements?.data?.total}</Tag>
          )}
        </span>
      ),
      children: <CompletedIslamicSigningTab />,
    },
    {
      key: 'pendingTrading',
      label: (
        <span>
          Pending Trading <Tag>{pendingTradingAgreements?.data?.total || 0}</Tag>
        </span>
      ),
      children: <PendingTradingTab />,
    },
  ];

  const fetchPendingAgreements = async (params?: any) => {
    await dispatch(getIslamicPendingSigningAgreementsThunk(params));
  };

  const fetchSignedAgreements = async (params?: any) => {
    await dispatch(getIslamicSignedAgreementsThunk(params));
  };

  const fetchPendingTradingAgreements = async (params?: any) => {
    await dispatch(getPendingTradingAgreementsThunk(params));
  };

  const onTabChange = (key: string) => {
    if (key === 'pendingSigning') {
      fetchPendingAgreements(initialParams);
    } else if (key === 'completedSigning') {
      fetchSignedAgreements(initialParams);
    } else if (key === 'pendingTrading') {
      fetchPendingTradingAgreements(initialParams);
    }

    queryParams.set('tab', key);
    queryParams.set('pageNum', `${initialParams.pagination.current}`);
    queryParams.set('pageSize', `${initialParams.pagination.pageSize}`);

    const newSearch = queryParams.toString();

    navigate({
      pathname: location.pathname,
      search: newSearch,
    });
  };

  useLifecycles(() => {
    const params = {
      ...initialParams,
      pagination: {
        current: pageNum ? +pageNum : initialParams.pagination.current,
        pageSize: pageSize ? +pageSize : initialParams.pagination.pageSize,
      },
    };

    fetchPendingAgreements(params);
    fetchSignedAgreements(params);
    fetchPendingTradingAgreements(params);
  });

  return (
    <>
      <PageHeader className="px-0 mb-2" title="Agreement Signing" />
      <Tabs
        defaultActiveKey={activeTab ?? 'pendingSigning'}
        onChange={(key) => onTabChange(key)}
        items={items}
        destroyInactiveTabPane
      />
    </>
  );
};

export default IslamicAgreementSigningListingPage;
